import { comparisonCalculator } from './ComparisonCalculator'
import { comparisonFilters } from './ComparisonFilters'
import { comparisonHistory } from './ComparisonHistory'
import { comparisonRanking } from './ComparisonRanking'
import { comparisonSorter } from './ComparisonSorter'
import type {
  ComparisonBuildResult,
  ComparisonFiltersState,
  ComparisonOfferInput,
  ComparisonPreferences,
  ComparisonSummary,
} from './ComparisonTypes'
import { DEFAULT_COMPARISON_FILTERS, DEFAULT_COMPARISON_PREFERENCES } from './ComparisonTypes'

/**
 * ComparisonEngine — group by fingerprint, compare, sort, economy.
 * Only compares Product Intelligence fingerprints (never raw text equality).
 */
export class ComparisonEngine {
  build(
    offers: ComparisonOfferInput[],
    filters: ComparisonFiltersState = DEFAULT_COMPARISON_FILTERS,
    preferences: ComparisonPreferences = DEFAULT_COMPARISON_PREFERENCES,
    selectedFingerprints: string[] = [],
  ): ComparisonBuildResult {
    let working = comparisonFilters.applyOffers(offers, filters)
    if (filters.hideDuplicates) {
      working = comparisonFilters.dedupeBySupplier(working)
    }

    const byFingerprint = new Map<string, ComparisonOfferInput[]>()
    for (const offer of working) {
      if (!offer.fingerprint) continue
      const list = byFingerprint.get(offer.fingerprint) ?? []
      list.push(offer)
      byFingerprint.set(offer.fingerprint, list)
    }

    let rows = [...byFingerprint.entries()].map(([fingerprint, group]) =>
      comparisonCalculator.buildRow(fingerprint, group),
    )

    rows = comparisonRanking.rankRows(rows, preferences)
    rows = comparisonSorter.sort(rows, preferences.sortMode, preferences.preferredProvider)

    const summary = this.buildSummary(rows, selectedFingerprints)

    return {
      rows,
      summary,
      suppliers: [...new Set(working.map((o) => o.supplierName))].sort(),
    }
  }

  snapshotPrices(offers: ComparisonOfferInput[]) {
    return comparisonHistory.recordOffers(offers)
  }

  private buildSummary(
    rows: ReturnType<ComparisonEngine['build']>['rows'],
    selectedFingerprints: string[],
  ): ComparisonSummary {
    const selected = rows.filter((r) => selectedFingerprints.includes(r.fingerprint))
    const selectedTotal = round2(
      selected.reduce((sum, row) => sum + (row.bestPrice ?? 0), 0),
    )
    const margins = selected
      .map((r) => r.bestMargin)
      .filter((m): m is number => m != null)
    const estimatedMargin =
      margins.length > 0 ? round2(margins.reduce((a, b) => a + b, 0) / margins.length) : null

    const supplierWins = new Map<string, number>()
    for (const row of rows) {
      if (!row.recommendedSupplier) continue
      supplierWins.set(
        row.recommendedSupplier,
        (supplierWins.get(row.recommendedSupplier) ?? 0) + 1,
      )
    }
    const recommendedSupplier =
      [...supplierWins.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null

    const bestPrices = rows.map((r) => r.bestPrice).filter((p): p is number => p != null)

    return {
      rowCount: rows.length,
      offerCount: rows.reduce((s, r) => s + r.offers.length, 0),
      bestPrice: bestPrices.length ? Math.min(...bestPrices) : null,
      totalEconomy: round2(rows.reduce((s, r) => s + r.economy, 0)),
      recommendedSupplier,
      selectedCount: selected.length,
      selectedTotal,
      estimatedMargin,
    }
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const comparisonEngine = new ComparisonEngine()
